import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T, { specularRing, specularHost, specularRingReduced } from '../home-new/tokens'
import Reveal from '../home-new/Reveal'

// Hero for /business — matches the design's hero band (hero_top.png): a large
// hero.png scene that BLEEDS off the TOP and RIGHT edges, filling roughly the
// right ~55-60% of the section, seamless with a subtle light background; the
// heading + subheading + two CTAs sit on the empty light LEFT ~40-45%.
//
// SIZING APPROACH (so pixel nudges are one-liners):
//  - Section: full-bleed width, subtle gradient (T.gradient.hero), overflow
//    hidden, min-height clamp(560px, 40vw, 720px) — this drives the image
//    size on desktop.
//  - Image: absolutely positioned top:0 right:0, height:100%, width:auto (so
//    it keeps hero.png's natural ~2:1 ratio and bleeds off the right; its own
//    empty light left-third overlaps the section bg invisibly). NO card, NO
//    radius, NO shadow, NO object-fit crop. Nudge the image bigger/smaller by
//    changing the section min-height; nudge the text clearance with copy
//    max-width (560 desktop / 480 at <1280px).
//  - Text: normal 1200px container, left column max-width 560, z-index 2,
//    vertically centered — clears the monitor because the image's real
//    content starts ~1/3 in from its own left edge.
//  - Stacks below 960px (md) — not 600 — because between 600–960 the tall
//    edge-bleed image would collide with the copy; stacking earlier keeps it
//    clean. Mobile: centered text on the light bg, image full-width contained
//    below, person fully visible.
const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    // Left-side soft light-blue wash (behind the text) fading to
    // transparent by ~58% where the photo sits, layered over a subtle
    // vertical near-white gradient. Reads clearly as a blue gradient on the
    // left like the design, while dark ink text stays well above AA.
    background:
      'linear-gradient(100deg, #D6E9FF 0%, #E3F1FF 28%, rgba(236,246,255,0.55) 46%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #EDF6FD 0%, #FFFFFF 58%)',
    minHeight: 'clamp(560px, 40vw, 720px)',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    paddingTop: 96,
    paddingBottom: 40,
    // Mobile (≤960px): instead of stacking, the hero.png becomes a full-bleed
    // COVER background (via the .art <img> below) with the copy overlaid on
    // top — matching the desktop spirit. A soft light-blue scrim (see
    // .container) keeps the ink text AA-readable over the busier cover crop.
    // Mobile "subject on top, content card at bottom" composition: the cover
    // image frames the woman's face in the UPPER half, and the frosted copy
    // panel is bottom-anchored (alignItems:flex-end) over the lower desk/body
    // area so it never hides her face. Tall min-height so both fit uncramped.
    [theme.breakpoints.down('md')]: {
      minHeight: 'clamp(640px, 138vw, 820px)',
      display: 'flex',
      alignItems: 'flex-end',
      paddingTop: 116,
      paddingBottom: 40,
      // Gentle BOTTOM wash: softly lightens the lower area behind the panel so
      // it reads as overlapping onto the light gradient below the scene —
      // fades UP to transparent by ~45% so the face up top stays crisp.
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        background:
          'linear-gradient(0deg, rgba(245,249,255,0.62) 0%, rgba(245,249,255,0.22) 26%, rgba(236,246,255,0) 45%)',
      },
    },
  },
  // Full edge-bleed banner scene, anchored to the top-right corner.
  art: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: 'auto',
    maxWidth: 'none',
    zIndex: 0,
    pointerEvents: 'none',
    userSelect: 'none',
    // Fade the photo's LEFT edge into the background gradient so there's no
    // hard vertical seam — the scene melts into the blue wash on the left
    // while the top/right bleed stays intact.
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 16%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 16%)',
    // Mobile: full-bleed COVER background instead of a contained block below.
    // object-position right-center keeps the woman + dashboard (which sit on
    // the RIGHT of the source scene) framed nicely in portrait, cropping the
    // emptier left edge rather than the subject.
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'block',
      height: '100%',
      width: '100%',
      maxWidth: 'none',
      margin: 0,
      objectFit: 'cover',
      objectPosition: '72% 16%',
      WebkitMaskImage: 'none',
      maskImage: 'none',
    },
  },
  container: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
  },
  copy: {
    maxWidth: 560,
    [theme.breakpoints.down('lg')]: {
      maxWidth: 480,
    },
    // Mobile: the copy sits inside a frosted light panel over the cover
    // image — separates the text from the busy photo so the heading/body/CTAs
    // stay crisp and AA-readable, and reads as premium. Light blue-white
    // family (no darkening). backdrop-filter degrades gracefully (the 0.78
    // white base alone still gives ink/inkSoft text ≥4.5:1).
    [theme.breakpoints.down('md')]: {
      maxWidth: 460,
      margin: '0 auto',
      textAlign: 'center',
      background: 'rgba(255,255,255,0.78)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.65)',
      borderRadius: 24,
      padding: '30px 26px 32px',
      boxShadow: '0 16px 44px rgba(0,102,204,0.16)',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 56,
    fontWeight: 700,
    lineHeight: 1.05,
    color: T.color.ink,
    letterSpacing: '0.5px',
    margin: '0 0 20px',
    whiteSpace: 'nowrap',
    // "BUSINESS" — reactbits "Gradient Text" (reactbits.dev/text-animations/
    // gradient-text) in pure CSS (no framer-motion — same React 16 constraint
    // as the LogoLoop). A blue-family gradient (first & last stop equal for a
    // seamless loop) flows across the letters: an oversized background-size +
    // a background-position ping-pong keyframe drift (~8s, reactbits default).
    // Progressive enhancement: solid `color` fallback FIRST, then the
    // background-clip:text reveal via `-webkit-text-fill-color:transparent`
    // (this — not `color:transparent` — keeps unsupported browsers showing
    // solid brand blue). Frozen under reduced-motion.
    '& span': {
      color: T.color.brand,
      backgroundImage:
        'linear-gradient(to right, #0066CC, #009EFF, #33C9FF, #66D9FF, #33C9FF, #009EFF, #0066CC)',
      backgroundSize: '300% 100%',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: '$businessFlow 8s linear infinite',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },
    [theme.breakpoints.down('md')]: {
      fontSize: 44,
      margin: '0 0 12px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 32,
    },
  },
  '@keyframes businessFlow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  lead: {
    fontFamily: T.font.heading,
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.45,
    color: T.color.ink,
    margin: '0 0 14px',
    [theme.breakpoints.down('md')]: {
      fontSize: 20,
      margin: '0 0 8px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 17,
    },
  },
  sub: {
    fontFamily: T.font.body,
    fontSize: 17,
    lineHeight: 1.7,
    color: T.color.inkSoft,
    margin: '0 0 32px',
    maxWidth: 520,
    [theme.breakpoints.down('md')]: {
      margin: '0 auto 24px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 15,
    },
  },
  ctaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
    },
    // Stack CTAs full-width with comfortable tap spacing on phones.
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '12px',
      '& > a': { width: '100%' },
    },
  },
  ctaPrimary: {
    ...specularHost,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    padding: '0 34px',
    borderRadius: T.radius.pill,
    background: T.gradient.blue,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 18,
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: '0 8px 24px rgba(0,102,204,0.28)',
    cursor: 'pointer',
    '&::before': specularRing(T.ring.blue, '1px'),
    '&:hover': { transform: 'translateY(-2px)', color: T.color.onBrand },
    '@media (prefers-reduced-motion: reduce)': specularRingReduced,
    [theme.breakpoints.down('sm')]: {
      minHeight: 48,
      fontSize: 16,
      padding: '0 26px',
    },
  },
  ctaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    padding: '0 34px',
    borderRadius: T.radius.pill,
    background: T.color.ink,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 18,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: T.shadow.md,
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      color: T.color.onBrand,
      boxShadow: T.shadow.lg,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 48,
      fontSize: 16,
      padding: '0 26px',
    },
  },
}))

const HeroBiz = () => {
  const classes = useStyles()
  const { t } = useTranslation('business')

  return (
    <section className={classes.root}>
      <Container
        maxWidth={false}
        style={{ maxWidth: T.container }}
        className={classes.container}
      >
        <div className={classes.copy}>
          <Reveal direction="left">
            <h1 className={classes.heading}>
              SHIPPOP <span>BUSINESS</span>
            </h1>
          </Reveal>
          <Reveal direction="left" delay={0.08}>
            <p className={classes.lead}>
              {t('hero.lead.1')}
              <br />
              {t('hero.lead.2')}
              <br />
              {t('hero.lead.3')}
            </p>
          </Reveal>
          <Reveal direction="left" delay={0.15}>
            <p className={classes.sub}>
              {t('hero.sub')}
            </p>
          </Reveal>
          <Reveal direction="up" delay={0.22}>
            <div className={classes.ctaRow}>
              <a href="#contact-form" className={classes.ctaPrimary}>
                {t('hero.ctaPrimary')}
              </a>
              <a href="#connect" className={classes.ctaSecondary}>
                {t('hero.ctaSecondary')}
              </a>
            </div>
          </Reveal>
        </div>
      </Container>
      <picture style={{ display: 'contents' }}>
        <source srcSet="/static/img/home-business/hero.webp" type="image/webp" />
        <img
          className={classes.art}
          src="/static/img/home-business/hero.png"
          alt={t('hero.imageAlt')}
          width={1400}
          height={700}
          fetchpriority="high"
          decoding="async"
        />
      </picture>
    </section>
  )
}

export default HeroBiz
